// Mapped nodes follow referenced shapes' live pose and path geometry. Automation and editor frames can update Pixi before Redux, so each node resolves through an env whose objects carry what Pixi currently draws. Pose-only updates re-place without rebaking; a changed raw-path version updates the outline and field.
import { onAutomationSample } from '~/lib/automation'
import { isShapeHeld } from '~/lib/map/held'
import { rawPathBounds } from '~/lib/objectBounds'
import { rawPathToString } from '~/lib/svg/paths'
import gsap from 'gsap'
import { Matrix } from 'pixi.js'
import { followLivePose } from './pixiShaderMesh'
import { clearMask } from './usePixiObjects'

const DEG = 180 / Math.PI
// Below this a live value is the stored one: the deg → rad → deg round trip alone must not re-place a node.
const EPS = 1e-6

const near = (a, b) => Math.abs(a - b) < EPS

// Live items reused while the pose holds, so the outline resolver's per-item cache hits frame after frame.
const items = new WeakMap()
const livePaths = new WeakMap()

const SHAPE_TYPES = new Set(['brush', 'path', 'rect', 'circle'])

/** `obj` at the pose `pixiObj` draws it; `parent` is the group container its `parentId` names (null when unmapped: a group the fade created), and a fade crosser held on the stage reads as a root object at its world pose. */
export const livePose = (obj, pixiObj, parent, liveData, grouped = !!parent) => {
	if (!obj || typeof obj !== 'object' || !pixiObj || pixiObj.destroyed) return obj
	const onStage = !!pixiObj.parent && !pixiObj.parent.parent
	const world = grouped && (parent ? pixiObj.parent !== parent : onStage)
	if (world && !onStage) return obj
	const live = { x: pixiObj.x, y: pixiObj.y, rotation: pixiObj.rotation * DEG }
	if (obj.type === 'group') {
		live.scaleX = pixiObj.scale.x
		live.scaleY = pixiObj.scale.y
	} else if (SHAPE_TYPES.has(obj.type)) {
		// A shape's scale is in its drawn points (`PixiShape.scaleX`); `_rawPath` stays unscaled.
		live.scaleX = pixiObj.scaleX * pixiObj.scale.x
		live.scaleY = pixiObj.scaleY * pixiObj.scale.y
		Object.assign(live, livePivot(obj, pixiObj))
	}
	const stored = key =>
		obj[key] ?? (key === 'scaleX' || key === 'scaleY' ? 1 : key === 'pivotX' || key === 'pivotY' ? 0.5 : 0)
	const keys = Object.keys(live).filter(k => Number.isFinite(live[k]) && (world || !near(live[k], stored(k))))
	const dataChanged = liveData != null && liveData !== obj.data
	if (!keys.length && !dataChanged) return obj
	const sig = `${world ? 'w|' : ''}${keys.map(k => `${k}${live[k]}`).join()}|${dataChanged ? livePathVersion(pixiObj) : ''}`
	const hit = items.get(obj)
	if (hit?.sig === sig) return hit.item
	const item = { ...obj }
	if (world) item.parentId = 'root'
	for (const k of keys) item[k] = live[k]
	if (dataChanged) item.data = liveData
	items.set(obj, { sig, item })
	return item
}

const livePathVersion = pixiObj => pixiObj._rawPathVersion ?? 0

const pivots = new WeakMap()

/** A fade tweens the pivot in absolute `data` units, so as a fraction of the morphing extent (`localExtent`: 0 to the max) it moves. */
const livePivot = (obj, pixiObj) => {
	const pv = obj.type !== 'brush' && pixiObj._unscaledPivot
	const raw = pv && pixiObj._rawPath
	if (!raw?.length) return null
	const version = livePathVersion(pixiObj)
	let ext = pivots.get(pixiObj)
	if (ext?.raw !== raw || ext.version !== version) {
		const b = rawPathBounds(raw)
		ext = { raw, version, w: Math.max(0, b.x + b.width), h: Math.max(0, b.y + b.height) }
		pivots.set(pixiObj, ext)
	}
	return { ...(ext.w > 0 && { pivotX: pv.x / ext.w }), ...(ext.h > 0 && { pivotY: pv.y / ext.h }) }
}

/** Objects by id as `base` (the store record or a Map) holds them, each with its live pose from `pixiMap` (id → `{ pixiObj }`). A Map, so the outline resolver reads it through `get`; read lazily, so one instance stays current frame after frame. */
export class LiveObjects extends Map {
	constructor(base, pixiMap) {
		super()
		this.base = base
		this.pixiMap = pixiMap
	}

	stored(id) {
		const { base } = this
		return base instanceof Map ? base.get(id) : base?.[id]
	}

	get(id) {
		const obj = this.stored(id)
		const entry = obj && this.pixiMap?.get(id)
		if (!entry) return obj
		const parentObj = obj.parentId != null ? this.stored(obj.parentId) : null
		const grouped = parentObj?.type === 'group'
		const parent = grouped ? (this.pixiMap.get(obj.parentId)?.pixiObj ?? null) : null
		const pixiObj = entry.pixiObj
		let liveData = null
		if (SHAPE_TYPES.has(obj.type) && pixiObj?._rawPath) {
			const version = livePathVersion(pixiObj)
			let cached = livePaths.get(pixiObj)
			if (!cached || cached.version !== version) {
				cached = { version, data: rawPathToString(pixiObj._rawPath) }
				livePaths.set(pixiObj, cached)
			}
			liveData = cached.data
		}
		return livePose(obj, pixiObj, parent, liveData, grouped)
	}

	has(id) {
		return this.stored(id) !== undefined
	}

	/** Pixi draws geometry for `id` that its store record does not hold yet: an editor drag's held path. */
	drafted(id) {
		const obj = this.stored(id)
		const pixiObj = obj && SHAPE_TYPES.has(obj.type) ? this.pixiMap?.get(id)?.pixiObj : null
		return pixiObj?._sourceData != null && pixiObj._sourceData !== obj.data
	}
}

// The env a node was built with, behind its live wrapper: `_mapping.env` is whichever placed it last.
const BASE = Symbol('liveBase')
const liveEnvs = new WeakMap()

/** `env` (`{ objects }`) with its objects at their live pose. One wrapper per env and map, so a still node keeps one env. */
export const liveEnv = (env, pixiMap) => {
	const base = env?.[BASE] ?? env
	if (!base || typeof base !== 'object') return base
	let byMap = liveEnvs.get(base)
	if (!byMap) liveEnvs.set(base, (byMap = new WeakMap()))
	let live = byMap.get(pixiMap)
	if (!live) {
		live = { objects: new LiveObjects(base.objects, pixiMap), [BASE]: base }
		byMap.set(pixiMap, live)
	}
	return live
}

/** A fill node shows what its shape shows, every frame: alpha (times its own `_fade`), visibility, blend, mask, parent, and the slot directly below `above`. */
export const followShape = (fill, shape, above = shape) => {
	if (!fill || fill.destroyed || !shape || shape.destroyed) return
	fill.alpha = shape.alpha * (fill._fade ?? 1)
	fill.visible = shape.visible && fill._shown !== false
	fill.blendMode = shape.blendMode
	const parent = shape.parent
	if (!parent) return
	if (fill.parent !== parent) parent.addChild(fill)
	const i = parent.getChildIndex(above.parent === parent && above !== fill ? above : shape)
	const j = parent.getChildIndex(fill)
	if (j !== i - 1) parent.setChildIndex(fill, j < i ? i - 1 : i)
	const mask = shape.mask
	const inverse = !!shape._maskInverse
	if (mask !== fill.mask || (mask && fill._maskInverse !== inverse)) {
		if (mask) fill.setMask({ mask, inverse })
		else clearMask(fill)
		fill._maskInverse = mask ? inverse : undefined
	}
	// Its draws are in stage space, so a group parent's transform is undone.
	if (parent.parent) {
		fill.setFromMatrix(parent.getGlobalTransform(groupMatrix, false).invert())
		fill._grouped = true
	} else if (fill._grouped) {
		fill.setFromMatrix(IDENTITY)
		fill._grouped = false
	}
}

const groupMatrix = new Matrix()
const IDENTITY = new Matrix()

/** `followShape`, then re-place the node's draws at the referenced shapes' live pose (`pixiMap`: id → `{ pixiObj }`). Returns whether the draws moved. */
export const followFill = (fill, shape, pixiMap, above = shape) => {
	followShape(fill, shape, above)
	const mapping = fill?._mapping
	if (!fill || fill.destroyed || !fill._surfaced || !mapping?.refs?.length || !mapping.env) return false
	if (mapping.refs.some(r => isShapeHeld(r?.id))) return false
	const key = fill._geometryKey
	followLivePose(fill, liveEnv(mapping.env, pixiMap))
	return fill._geometryKey !== key
}

/** Every shape's fill node in `pixiMap` follows its shape (`followFill`). Returns how many re-placed. */
export const followLivePoses = pixiMap => {
	if (!pixiMap?.size) return 0
	let moved = 0
	for (const entry of pixiMap.values()) if (entry?.fill && followFill(entry.fill, entry.pixiObj, pixiMap)) moved++
	return moved
}

/** Follow live poses on every gsap frame (after the fade tweens, which gsap updates first) and after every automation sample (written after the gsap frame, before the render). Returns the stop. */
export const startLivePoseFollow = getPixiMap => {
	const follow = () => followLivePoses(getPixiMap())
	gsap.ticker.add(follow)
	const offSamples = onAutomationSample(follow)
	return () => {
		gsap.ticker.remove(follow)
		offSamples()
	}
}
